import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icons that break under bundlers because Leaflet resolves
// them via relative asset paths. Point to the CDN copies once at module load.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:      "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Keep the map view in sync when the parent's lat/lng inputs change (typed
// by hand). Uses `flyTo` for a soft animation without re-mounting the map.
function RecenterOnChange({ lat, lng }) {
  const map = useMap();
  const prev = useRef([lat, lng]);
  useEffect(() => {
    if (!isFinite(lat) || !isFinite(lng)) return;
    if (prev.current[0] !== lat || prev.current[1] !== lng) {
      map.flyTo([lat, lng], map.getZoom(), { duration: 0.4 });
      prev.current = [lat, lng];
    }
  }, [lat, lng, map]);
  return null;
}

/**
 * AttendanceLocationMap — mini Leaflet preview for the Master Lokasi Absensi
 * dialog. Renders a draggable pin at (lat, lng) with a translucent circle
 * whose radius reflects `radiusMeter` live. `onChange({lat, lng})` fires
 * when the admin drags the pin so the parent inputs stay in sync.
 */
export default function AttendanceLocationMap({ lat, lng, radiusMeter, onChange }) {
  const latN = Number(lat);
  const lngN = Number(lng);
  const rM = Math.max(10, Number(radiusMeter) || 100);
  const valid = isFinite(latN) && isFinite(lngN);
  if (!valid) {
    return (
      <div className="h-56 flex items-center justify-center text-xs text-[#9CA3AF] rounded-md border border-dashed border-[#E5E7EB] bg-[#F8F9FA]">
        Isi Latitude & Longitude untuk melihat peta.
      </div>
    );
  }
  return (
    <div className="rounded-md overflow-hidden border border-[#E5E7EB]" data-testid="attendance-loc-map">
      <MapContainer
        center={[latN, lngN]}
        zoom={17}
        scrollWheelZoom={false}
        style={{ height: 240, width: "100%" }}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <RecenterOnChange lat={latN} lng={lngN} />
        <Marker
          position={[latN, lngN]}
          draggable
          eventHandlers={{
            dragend: (e) => {
              const p = e.target.getLatLng();
              onChange?.({ lat: p.lat, lng: p.lng });
            },
          }}
        />
        <Circle
          center={[latN, lngN]}
          radius={rM}
          pathOptions={{ color: "#0055FF", fillColor: "#0055FF", fillOpacity: 0.15, weight: 2 }}
        />
      </MapContainer>
      <div className="px-2 py-1.5 text-[10px] text-[#6B7280] bg-[#F8F9FA] border-t border-[#E5E7EB] flex items-center justify-between gap-2">
        <span className="font-mono-tab">{latN.toFixed(5)}, {lngN.toFixed(5)}</span>
        <span>Radius <b className="font-mono-tab">{rM}m</b> · Geser pin untuk memindahkan lokasi</span>
      </div>
    </div>
  );
}
